import { CheckIcon, ChevronDownIcon } from '@shared/assets/icons';
import { RadioButton } from '@shared/ui/kit';
import InputSearch from '@shared/ui/input-search';
import { UnstyledButton } from '@shared/ui/unstyled-button';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { colors } from '@shared/tokens/colors.stylex';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles';
import { DropdownProps } from './types';
import { useCustomScrollbar } from '@shared/hooks/useCustomScrollbar';
import { scrollbarStyles } from './scrollbar/styles';

export const CustomDropdown = <T extends Record<string, unknown>>({
  placeholder = 'Выберите значение',
  labelMessage,
  options,
  itemText,
  itemValue,
  value = null,
  changeFunc,
  settings = {},
  isRadio = true,
  fullWidth,
  searchable = false,
  searchPlaceholder,
  className,
  errorMessage,
  errorFloating = false,
  listMaxHeight,
  isDisabled = false,
}: DropdownProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Закрытие при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Получение отображаемого текста для выбранного значения
  const getDisplayText = useCallback(() => {
    if (!value) {
      return placeholder;
    }

    const currentValue = value as T;
    return String(currentValue[itemText] ?? '');
  }, [value, itemText, placeholder]);

  // Проверка, выбрана ли опция
  const isOptionSelected = useCallback(
    (option: T): boolean => {
      if (!value) return false;

      const currentValue = value as T;

      if (itemValue) {
        return currentValue[itemValue] === option[itemValue];
      }

      return currentValue === option;
    },
    [value, itemValue]
  );

  // Обработка выбора опции
  const handleOptionClick = useCallback(
    (option: T) => {
      if (isDisabled) return;

      changeFunc?.(option);
      setIsOpen(false);
    },
    [changeFunc, isDisabled]
  );

  const handleDropdownButtonClick = useCallback(() => {
    if (isDisabled) return;

    setIsOpen((prev) => !prev);
  }, [isDisabled]);

  const filteredOptions = useMemo(
    () =>
      searchable && searchQuery.trim()
        ? options.filter((option) =>
            String(option[itemText] ?? '')
              .toLowerCase()
              .includes(searchQuery.trim().toLowerCase())
          )
        : options,
    [searchable, searchQuery, options, itemText]
  );

  const handleDropdownItemClick = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      const index = Number(event.currentTarget.dataset.index);
      const option = filteredOptions[index];

      if (!option) {
        return;
      }

      handleOptionClick(option);
    },
    [filteredOptions, handleOptionClick]
  );

  const displayText = getDisplayText();
  const hasValue = Boolean(value);
  const showError = Boolean(errorMessage);
  const { scrollRef: listScrollRef, customScrollbar } = useCustomScrollbar<HTMLUListElement>(isOpen);

  const dropdownClassName = [
    stylex.props(
      styles.dropdownContainer,
      fullWidth && styles.fullWidth,
      isDisabled && styles.dropdownContainerDisabled
    ).className,
    className,
  ].join(' ');

  return (
    <div className={dropdownClassName} ref={dropdownRef} style={settings}>
      {labelMessage && (
        <label {...stylex.props(styles.label, isDisabled && styles.labelDisabled)}>{labelMessage}</label>
      )}

      <div
        {...stylex.props(
          styles.dropdownTriggerWrap,
          fullWidth && styles.fullWidth,
          isDisabled && styles.dropdownTriggerWrapDisabled
        )}
      >
        <UnstyledButton
          {...stylex.props(
            styles.dropdownButton,
            styles.dropdownButtonSmall,
            fullWidth && styles.fullWidth,
            showError && styles.dropdownButtonError,
            isDisabled && styles.dropdownButtonDisabled
          )}
          onClick={handleDropdownButtonClick}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-disabled={isDisabled}
          disabled={isDisabled}
        >
          <span
            {...stylex.props(
              !hasValue && (isDisabled ? styles.dropdownButtonTextDisabled : styles.dropdownButtonTextPlaceholder)
            )}
          >
            {displayText}
          </span>

          <div {...stylex.props(styles.dropdownButtonIcons)}>
            <div {...stylex.props(styles.chevronIcon, isOpen && styles.chevronIconOpen)}>
              <ChevronDownIcon size={16} />
            </div>
          </div>
        </UnstyledButton>

        {showError && errorFloating && <div {...stylex.props(styles.errorTextFloating)}>{errorMessage}</div>}
      </div>

      {showError && !errorFloating && <div {...stylex.props(styles.errorText)}>{errorMessage}</div>}

      {isOpen && (
        <div
          {...stylex.props(styles.dropdownList)}
          style={{
            zIndex: settings.zIndex ? Number(settings.zIndex) : 999,
            ...(listMaxHeight ? { maxHeight: listMaxHeight } : {}),
          }}
        >
          {searchable && (
            <div {...stylex.props(styles.searchInputWrap)}>
              <InputSearch
                searchValue={searchQuery}
                changeValue={setSearchQuery}
                clearHandler={() => setSearchQuery('')}
                placeholder={searchPlaceholder ?? ''}
              />
            </div>
          )}
          <div {...stylex.props(styles.dropdownListScroll)}>
            <div {...stylex.props(scrollbarStyles.host)}>
              <ul ref={listScrollRef} {...stylex.props(styles.dropdownListUl, scrollbarStyles.scroll)}>
                {filteredOptions.map((option, index) => {
                  const isSelected = isOptionSelected(option);
                  const optionText = String(option[itemText] ?? '');

                  return (
                    <li key={index} {...stylex.props(styles.dropdownItem)}>
                      <UnstyledButton
                        {...stylex.props(styles.dropdownItemButton, isSelected && styles.dropdownItemSelected)}
                        onClick={handleDropdownItemClick}
                        data-index={index}
                      >
                        {isRadio && (
                          <div {...stylex.props(styles.radioWrapper)}>
                            <RadioButton
                              isChecked={isSelected}
                              changeFunction={() => handleOptionClick(option)}
                              size="medium"
                            />
                          </div>
                        )}
                        <span {...stylex.props(isSelected && !isRadio && styles.dropdownItemSelectedText)}>
                          {optionText}
                        </span>
                        {isSelected && !isRadio && <CheckIcon width="20" height="20" color={colors.accentIcon} />}
                      </UnstyledButton>
                    </li>
                  );
                })}
              </ul>
              {customScrollbar.visible && (
                <div {...stylex.props(scrollbarStyles.track)}>
                  <div
                    {...stylex.props(scrollbarStyles.thumb)}
                    style={{
                      height: `${customScrollbar.thumbHeight}px`,
                      transform: `translateY(${customScrollbar.thumbTop}px)`,
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
